import { initializeApp, getApps } from 'firebase/app';
import { getAnalytics, logEvent, Analytics } from 'firebase/analytics';

// Firebase configuration (you'll need to replace with your actual config)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "your-api-key",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "your-project.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "your-project-id",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "your-project.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:123456789:web:abcdef",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-ABCDEFGHIJ"
};

// Initialize Firebase
let app;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Initialize Analytics
let analytics: Analytics | null = null;
if (typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app);
    console.log('Firebase Analytics initialized successfully');
  } catch (error) {
    console.warn('Failed to initialize Firebase Analytics:', error);
  }
}

// Frontend Analytics Service
export class FrontendAnalytics {
  private static instance: FrontendAnalytics;
  private analytics: Analytics | null = null;
  private userId: string | null = null;

  private constructor() {
    this.analytics = analytics;
  }

  public static getInstance(): FrontendAnalytics {
    if (!FrontendAnalytics.instance) {
      FrontendAnalytics.instance = new FrontendAnalytics();
    }
    return FrontendAnalytics.instance;
  }

  public setUserId(userId: string | null) {
    this.userId = userId;
  }

  // Track page views
  public trackPageView(pageName: string, additionalData?: Record<string, any>) {
    if (!this.analytics) {
      console.warn('Analytics not initialized');
      return;
    }

    try {
      logEvent(this.analytics, 'page_view', {
        page_title: pageName,
        user_id: this.userId,
        timestamp: Date.now(),
        ...additionalData
      });

      console.log(`📊 [Frontend Analytics] Page view: ${pageName}`, {
        userId: this.userId,
        ...additionalData
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  // Track user interactions
  public trackUserInteraction(action: string, target: string, additionalData?: Record<string, any>) {
    if (!this.analytics) {
      console.warn('Analytics not initialized');
      return;
    }

    try {
      logEvent(this.analytics, 'user_interaction', {
        action,
        target,
        user_id: this.userId,
        timestamp: Date.now(),
        ...additionalData
      });

      console.log(`📊 [Frontend Analytics] User interaction: ${action} on ${target}`, {
        userId: this.userId,
        ...additionalData
      });
    } catch (error) {
      console.error('Failed to track user interaction:', error);
    }
  }

  // Track form submissions
  public trackFormSubmission(formName: string, success: boolean, additionalData?: Record<string, any>) {
    if (!this.analytics) {
      console.warn('Analytics not initialized');
      return;
    }

    try {
      logEvent(this.analytics, 'form_submission', {
        form_name: formName,
        success,
        user_id: this.userId,
        timestamp: Date.now(),
        ...additionalData
      });

      console.log(`📊 [Frontend Analytics] Form submission: ${formName} (${success ? 'success' : 'failed'})`, {
        userId: this.userId,
        ...additionalData
      });
    } catch (error) {
      console.error('Failed to track form submission:', error);
    }
  }

  // Track API calls from frontend
  public trackAPICall(
    method: string, 
    endpoint: string, 
    success: boolean, 
    responseTime?: number,
    additionalData?: Record<string, any>
  ) {
    if (!this.analytics) {
      console.warn('Analytics not initialized');
      return;
    }

    try {
      logEvent(this.analytics, 'api_call', {
        method,
        endpoint,
        success,
        response_time: responseTime,
        user_id: this.userId,
        timestamp: Date.now(),
        ...additionalData
      });

      console.log(`📊 [Frontend Analytics] API call: ${method} ${endpoint} (${success ? 'success' : 'failed'})`, {
        userId: this.userId,
        responseTime: responseTime ? `${responseTime}ms` : 'unknown',
        ...additionalData
      });
    } catch (error) {
      console.error('Failed to track API call:', error);
    }
  }

  // Track custom events
  public trackCustomEvent(eventName: string, properties: Record<string, any> = {}) {
    if (!this.analytics) {
      console.warn('Analytics not initialized');
      return;
    }

    try {
      logEvent(this.analytics, eventName, {
        user_id: this.userId,
        timestamp: Date.now(),
        ...properties
      });

      console.log(`📊 [Frontend Analytics] Custom event: ${eventName}`, {
        userId: this.userId,
        ...properties
      });
    } catch (error) {
      console.error('Failed to track custom event:', error);
    }
  }

  // Track navigation/routing
  public trackNavigation(from: string, to: string, method: 'click' | 'programmatic' = 'click') {
    if (!this.analytics) {
      console.warn('Analytics not initialized');
      return;
    }

    try {
      logEvent(this.analytics, 'navigation', {
        from_page: from,
        to_page: to,
        method,
        user_id: this.userId,
        timestamp: Date.now()
      });

      console.log(`📊 [Frontend Analytics] Navigation: ${from} → ${to} (${method})`, {
        userId: this.userId
      });
    } catch (error) {
      console.error('Failed to track navigation:', error);
    }
  }

  // Track errors
  public trackError(error: Error | string, context?: string, additionalData?: Record<string, any>) {
    if (!this.analytics) {
      console.warn('Analytics not initialized');
      return;
    }

    try {
      const errorMessage = error instanceof Error ? error.message : error;
      const errorStack = error instanceof Error ? error.stack : undefined;

      logEvent(this.analytics, 'error_occurred', {
        error_message: errorMessage,
        error_stack: errorStack,
        context,
        user_id: this.userId,
        timestamp: Date.now(),
        ...additionalData
      });

      console.log(`📊 [Frontend Analytics] Error tracked: ${errorMessage}`, {
        userId: this.userId,
        context,
        ...additionalData
      });
    } catch (trackingError) {
      console.error('Failed to track error:', trackingError);
    }
  }
}

export const frontendAnalytics = FrontendAnalytics.getInstance();